// Phase 15b — Route Engine planner (slice 2, Option A).
//
// First real consumer of the 15a domain module. A self-contained,
// additive planning surface: pick one of the 7 strategy postures and
// see its deterministic projection. No I/O, no agents, no writes —
// it only renders calculateStrategyRoutePlan output. The honesty
// fields (probability + downside + infeasibility) are surfaced
// verbatim; nothing here promises an outcome (MASTER Non-Neg #9).
//
// multi_market is selectable-but-disabled with its phase18 note —
// shown honestly, never a dead control.

import { useMemo, useState } from "react";

import {
  calculateStrategyRoutePlan,
  STRATEGY_ROUTE_PROFILES,
  STRATEGY_ROUTES,
  type StrategyRoute,
} from "../../strategy/strategyRoute.ts";
import { buildStrategyReportInsert } from "../../reports/strategyReport.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

type SaveState = "idle" | "saving" | "saved" | "error";

const WRAP: React.CSSProperties = {
  maxWidth: 1080,
  margin: "0 auto",
  padding: "32px 28px 80px",
  color: "var(--se-text)",
};
const EYEBROW: React.CSSProperties = {
  fontFamily: "var(--se-font-mono)",
  fontSize: 10,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--se-text-muted)",
};
const FIELD_LABEL: React.CSSProperties = {
  ...EYEBROW,
  marginBottom: 6,
  display: "block",
};
const INPUT: React.CSSProperties = {
  background: "var(--se-surface-raised)",
  border: "1px solid var(--se-border)",
  borderRadius: 3,
  color: "var(--se-text)",
  padding: "9px 11px",
  fontFamily: "var(--se-font-mono)",
  fontSize: 13,
  width: 140,
};

function NumberField(
  { label, value, onChange, min }: {
    label: string;
    value: number;
    onChange: (v: number) => void;
    min: number;
  },
): JSX.Element {
  return (
    <label style={{ display: "flex", flexDirection: "column" }}>
      <span style={FIELD_LABEL}>{label}</span>
      <input
        type="number"
        min={min}
        value={value}
        data-testid={`strategy-input-${label.toLowerCase().replace(/\s+/g, "-")}`}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        style={INPUT}
      />
    </label>
  );
}

export function StrategyPlannerPage(): JSX.Element {
  const [targetAmount, setTargetAmount] = useState(5000);
  const [openingStake, setOpeningStake] = useState(100);
  const [maxWeeks, setMaxWeeks] = useState(20);
  const [route, setRoute] = useState<StrategyRoute>("balanced");

  const plan = useMemo(() => {
    try {
      return calculateStrategyRoutePlan(
        { targetAmount, openingStake, maxWeeks },
        route,
      );
    } catch (e) {
      return e instanceof Error ? e.message : "Invalid input.";
    }
  }, [targetAmount, openingStake, maxWeeks, route]);

  const profile = STRATEGY_ROUTE_PROFILES[route];

  const [saveState, setSaveState] = useState<SaveState>("idle");

  // Lazy + browser-only: keeps the page SSR-pure (no auth hook at
  // render). The owner-checked INSERT policy (migration p15d) is the
  // real security boundary; user_id is auth.uid()'s, never trusted
  // input.
  async function saveAsReport(): Promise<void> {
    if (typeof plan === "string") return;
    setSaveState("saving");
    try {
      const client = getBrowserClient();
      const { data: { user } } = await client.auth.getUser();
      if (!user) {
        setSaveState("error");
        return;
      }
      const payload = buildStrategyReportInsert(plan, route);
      const { error } = await client
        .from("reports")
        .insert({ ...payload, user_id: user.id });
      setSaveState(error ? "error" : "saved");
    } catch {
      setSaveState("error");
    }
  }

  const [ladderState, setLadderState] = useState<SaveState>("idle");

  // Founder moat ruling (memory: project_strategy_route_override):
  // this posture OVERRIDES the agent's target odds in the Ladder
  // pipeline so each user's selections diverge. Write goes through the
  // security-definer set_my_ladder_posture RPC (ld_user_budgets is
  // service-role-only); RPC returns false if the user has no Ladder
  // budget yet — surfaced honestly, not as success.
  async function useForLadder(): Promise<void> {
    setLadderState("saving");
    try {
      const client = getBrowserClient();
      const { data, error } = await (client.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: unknown }>)(
        "set_my_ladder_posture",
        { p_route: route },
      );
      setLadderState(!error && data === true ? "saved" : "error");
    } catch {
      setLadderState("error");
    }
  }

  const [singlesState, setSinglesState] = useState<SaveState>("idle");

  // Same founder moat ruling, Singles surface. set_my_singles_posture
  // is the security-definer write to sc_reserves (service-role-only);
  // false ⇒ no singles reserve yet — surfaced honestly.
  async function useForSingles(): Promise<void> {
    setSinglesState("saving");
    try {
      const client = getBrowserClient();
      const { data, error } = await (client.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: unknown }>)(
        "set_my_singles_posture",
        { p_route: route },
      );
      setSinglesState(!error && data === true ? "saved" : "error");
    } catch {
      setSinglesState("error");
    }
  }

  return (
    <div data-testid="strategy-planner" style={WRAP}>
      <div style={EYEBROW}>Route Engine · Phase 15</div>
      <h1
        style={{
          fontFamily: "var(--se-font-display)",
          fontSize: 26,
          fontWeight: 500,
          margin: "10px 0 6px",
          letterSpacing: "-0.01em",
        }}
      >
        Strategy route planner
      </h1>
      <p
        style={{
          fontFamily: "var(--se-font-body)",
          fontSize: 13,
          color: "var(--se-text-sec)",
          maxWidth: 620,
          lineHeight: 1.55,
          margin: "0 0 28px",
        }}
      >
        Pick a posture and see its deterministic projection. These are
        planning assumptions, not historical performance and not a
        promise — every route shows its probability and its downside.
      </p>

      {/* Route selector */}
      <div style={{ ...EYEBROW, marginBottom: 10 }}>Posture</div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 26,
        }}
      >
        {STRATEGY_ROUTES.map((r) => {
          const p = STRATEGY_ROUTE_PROFILES[r];
          const active = r === route;
          const disabled = p.phase18Dependent;
          return (
            <button
              key={r}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && setRoute(r)}
              data-testid={`strategy-route-${r}`}
              data-active={active || undefined}
              title={disabled
                ? "Reserved for Phase 18 (multi-market expansion)."
                : p.intent}
              style={{
                padding: "8px 14px",
                borderRadius: 2,
                border: `1px solid ${
                  active ? "var(--se-text)" : "var(--se-border)"
                }`,
                background: active
                  ? "var(--se-text)"
                  : "transparent",
                color: disabled
                  ? "var(--se-text-muted)"
                  : active
                    ? "var(--se-surface-page)"
                    : "var(--se-text-sec)",
                fontFamily: "var(--se-font-body)",
                fontSize: 12.5,
                fontWeight: active ? 600 : 400,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
              }}
            >
              {p.label}
              {disabled ? " · Phase 18" : ""}
            </button>
          );
        })}
      </div>

      <div
        data-testid="strategy-route-intent"
        style={{
          fontFamily: "var(--se-font-body)",
          fontSize: 13,
          color: "var(--se-text-sec)",
          marginBottom: 24,
        }}
      >
        {profile.intent}
      </div>

      {/* Inputs */}
      <div style={{ display: "flex", gap: 18, marginBottom: 30 }}>
        <NumberField
          label="Target amount"
          value={targetAmount}
          min={1}
          onChange={setTargetAmount}
        />
        <NumberField
          label="Opening stake"
          value={openingStake}
          min={1}
          onChange={setOpeningStake}
        />
        <NumberField
          label="Max weeks"
          value={maxWeeks}
          min={1}
          onChange={setMaxWeeks}
        />
      </div>

      {/* Projection */}
      {typeof plan === "string" ? (
        <div
          data-testid="strategy-error"
          style={{
            border: "1px solid var(--se-conflict-amber)",
            borderRadius: 4,
            padding: "16px 18px",
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
            color: "var(--se-text-sec)",
          }}
        >
          {plan}
        </div>
      ) : (
        <div
          data-testid="strategy-projection"
          style={{
            border: "1px solid var(--se-border)",
            borderRadius: 4,
            background: "var(--se-surface-card)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 20,
              padding: "20px 22px",
              borderBottom: "1px solid var(--se-border)",
            }}
          >
            <Figure
              label="Estimated weeks"
              value={plan.estimatedWeeks === null
                ? "—"
                : String(plan.estimatedWeeks)}
            />
            <Figure
              label="Chain probability"
              value={`${(plan.fullChainProbability * 100).toFixed(1)}%`}
            />
            <Figure
              label="Projected final"
              value={plan.projectedFinalAmount.toFixed(2)}
            />
            <Figure
              label="Protected at bust"
              value={plan.protectedAmountAtBust.toFixed(2)}
            />
          </div>
          <div
            data-testid="strategy-downside"
            style={{
              padding: "16px 22px",
              fontFamily: "var(--se-font-body)",
              fontSize: 12.5,
              color: plan.feasible
                ? "var(--se-text-sec)"
                : "var(--se-drop-red)",
              lineHeight: 1.55,
              borderBottom: "1px solid var(--se-border)",
            }}
          >
            {plan.downsideSummary}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 22px",
              borderBottom: "1px solid var(--se-border)",
            }}
          >
            <button
              type="button"
              data-testid="strategy-save-report"
              disabled={saveState === "saving" || saveState === "saved"}
              onClick={() => void saveAsReport()}
              style={{
                padding: "7px 14px",
                borderRadius: 2,
                border: "1px solid var(--se-border)",
                background: "transparent",
                color: "var(--se-text-sec)",
                fontFamily: "var(--se-font-body)",
                fontSize: 12,
                cursor: saveState === "saving" || saveState === "saved"
                  ? "default"
                  : "pointer",
              }}
            >
              {saveState === "saving"
                ? "Saving…"
                : saveState === "saved"
                  ? "Saved to Reports ✓"
                  : "Save as report"}
            </button>
            {saveState === "error" ? (
              <span
                data-testid="strategy-save-error"
                style={{
                  fontFamily: "var(--se-font-body)",
                  fontSize: 12,
                  color: "var(--se-drop-red)",
                }}
              >
                Could not save — sign in and try again.
              </span>
            ) : null}

            <button
              type="button"
              data-testid="strategy-use-for-ladder"
              disabled={ladderState === "saving" || ladderState === "saved"}
              onClick={() => void useForLadder()}
              title="Your Ladder picks will be built to this posture's target odds — diverging from other users."
              style={{
                padding: "7px 14px",
                borderRadius: 2,
                border: "1px solid var(--se-amber)",
                background: "transparent",
                color: "var(--se-amber)",
                fontFamily: "var(--se-font-body)",
                fontSize: 12,
                cursor: ladderState === "saving" || ladderState === "saved"
                  ? "default"
                  : "pointer",
              }}
            >
              {ladderState === "saving"
                ? "Applying…"
                : ladderState === "saved"
                  ? "Ladder posture set ✓"
                  : "Use as my Ladder posture"}
            </button>
            {ladderState === "error" ? (
              <span
                data-testid="strategy-ladder-error"
                style={{
                  fontFamily: "var(--se-font-body)",
                  fontSize: 12,
                  color: "var(--se-drop-red)",
                }}
              >
                No active Ladder budget, or not signed in.
              </span>
            ) : null}

            <button
              type="button"
              data-testid="strategy-use-for-singles"
              disabled={singlesState === "saving" || singlesState === "saved"}
              onClick={() => void useForSingles()}
              title="Your Singles slots will be built to this posture's target odds — diverging from other users."
              style={{
                padding: "7px 14px",
                borderRadius: 2,
                border: "1px solid var(--se-amber)",
                background: "transparent",
                color: "var(--se-amber)",
                fontFamily: "var(--se-font-body)",
                fontSize: 12,
                cursor: singlesState === "saving" || singlesState === "saved"
                  ? "default"
                  : "pointer",
              }}
            >
              {singlesState === "saving"
                ? "Applying…"
                : singlesState === "saved"
                  ? "Singles posture set ✓"
                  : "Use as my Singles posture"}
            </button>
            {singlesState === "error" ? (
              <span
                data-testid="strategy-singles-error"
                style={{
                  fontFamily: "var(--se-font-body)",
                  fontSize: 12,
                  color: "var(--se-drop-red)",
                }}
              >
                No active Singles reserve, or not signed in.
              </span>
            ) : null}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "var(--se-font-mono)",
                fontSize: 11.5,
              }}
            >
              <thead>
                <tr style={{ color: "var(--se-text-muted)" }}>
                  {["Wk", "Stake", "Gross", "Protected", "Running"].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: "left",
                          padding: "10px 22px",
                          fontWeight: 400,
                          letterSpacing: "0.08em",
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {plan.weekRows.map((w) => (
                  <tr
                    key={w.week}
                    style={{ borderTop: "1px solid var(--se-border)" }}
                  >
                    <td style={{ padding: "8px 22px" }}>{w.week}</td>
                    <td style={{ padding: "8px 22px" }}>
                      {w.startingStake.toFixed(2)}
                    </td>
                    <td style={{ padding: "8px 22px" }}>
                      {w.grossReturn.toFixed(2)}
                    </td>
                    <td style={{ padding: "8px 22px" }}>
                      {w.protectedAmount.toFixed(2)}
                    </td>
                    <td style={{ padding: "8px 22px" }}>
                      {w.runningProtected.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Figure(
  { label, value }: { label: string; value: string },
): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={EYEBROW}>{label}</span>
      <span
        style={{
          fontFamily: "var(--se-font-display)",
          fontSize: 18,
          fontWeight: 500,
        }}
      >
        {value}
      </span>
    </div>
  );
}
